import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { PageHero, Section } from "@/components/site/section";
import { FixtureCard, NewsCard, StatTile } from "@/components/site/cards";
import { fixturesQuery, newsByCategoryQuery, squadQuery } from "@/lib/site-queries";

export const Route = createFileRoute("/national-team/")({
  head: () => ({
    meta: [
      { title: "Argentina National Football Team | Argentinian.Soccer" },
      {
        name: "description",
        content: "Argentina National Team news, fixtures, squad and statistics in one place.",
      },
      { property: "og:title", content: "Argentina National Football Team | Argentinian.Soccer" },
      { property: "og:url", content: "/national-team" },
    ],
    links: [{ rel: "canonical", href: "/national-team" }],
  }),
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(newsByCategoryQuery("national_team", 6)),
      context.queryClient.ensureQueryData(fixturesQuery("senior", 5)),
      context.queryClient.ensureQueryData(squadQuery("senior")),
    ]),
  component: NTHub,
});

function NTHub() {
  const { data: news } = useSuspenseQuery(newsByCategoryQuery("national_team", 6));
  const { data: fixtures } = useSuspenseQuery(fixturesQuery("senior", 5));
  const { data: squad } = useSuspenseQuery(squadQuery("senior"));
  const totalCaps = squad.reduce((acc, p) => acc + (p.caps ?? 0), 0);
  const totalGoals = squad.reduce((acc, p) => acc + (p.goals ?? 0), 0);

  return (
    <>
      <PageHero
        eyebrow="National Team"
        title="Argentina National Football Team"
        description="World champions. La Albiceleste — covered in full."
      />

      <Section id="news" eyebrow="National Team" title="News">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {news.map((a) => (
            <NewsCard key={a.id} article={a} />
          ))}
        </div>
      </Section>

      <Section id="fixtures" eyebrow="Fixtures" title="Fixtures">
        <div className="grid gap-5 md:grid-cols-2">
          {fixtures.map((f) => (
            <FixtureCard key={f.id} fixture={f} />
          ))}
        </div>
        <Link to="/national-team/fixtures" className="mt-4 inline-flex text-sm font-semibold text-primary">
          All fixtures →
        </Link>
      </Section>

      <Section id="squad" eyebrow="Squad" title="Squad">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {squad.slice(0, 12).map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-border bg-card p-4">
              <div>
                <p className="font-display text-base font-bold text-navy">{p.name}</p>
                <p className="text-xs text-muted-foreground">{p.position} · {p.club}</p>
              </div>
              <span className="font-display text-2xl font-bold text-primary">{p.shirt_number}</span>
            </div>
          ))}
        </div>
        <Link to="/national-team/squad" className="mt-4 inline-flex text-sm font-semibold text-primary">
          Full squad →
        </Link>
      </Section>

      <Section id="statistics" eyebrow="Statistics" title="Statistics">
        <div className="grid gap-4 md:grid-cols-3">
          <StatTile label="Players in squad" value={squad.length} />
          <StatTile label="Combined caps" value={totalCaps} />
          <StatTile label="Combined goals" value={totalGoals} />
        </div>
        <Link to="/national-team/statistics" className="mt-4 inline-flex text-sm font-semibold text-primary">
          Detailed statistics →
        </Link>
      </Section>
    </>
  );
}
